// components/common/bulkImportDrawer.js
import React, { useEffect, useMemo } from "react";
import {
  Offcanvas,
  Button,
  Spinner,
  Alert
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faFileImport,
  faCheckCircle,
  faTimesCircle,
  faCloudUploadAlt,
  faExclamationCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { downloadTemplate } from "../../utils/importParser";
import useBulkImport from "../../hooks/useBulkImport";
import { toast } from "react-toastify";

const getUniformErrors = (invalidRows = []) => {
  if (invalidRows.length < 2) return [];
  const signature = (errors) => [...new Set(errors || [])].sort().join("||");
  const first = signature(invalidRows[0].errors);
  const isUniform = invalidRows.every((r) => signature(r.errors) === first);
  return isUniform ? [...new Set(invalidRows[0].errors || [])] : [];
};

const BulkImportDrawer = ({ config, onImportComplete }) => {
  const { t } = useTranslation();

  const {
    isOpen,
    selectedFile,
    validationResult,
    isLoading,
    error,
    openDrawer,
    closeDrawer,
    handleFileSelect,
    validateFile,
    executeImport,
    setError,
  } = useBulkImport(config, onImportComplete);

  const hasImportErrors = validationResult?.invalidRows?.length > 0;
  const hasDuplicates = !hasImportErrors && validationResult?.totalDuplicates > 0;
  const uniformErrors = useMemo(
    () => getUniformErrors(validationResult?.invalidRows),
    [validationResult]
  );

  const allRowsInvalid =
    hasImportErrors &&
    validationResult.totalValid === 0 &&
    !(validationResult.totalDuplicates > 0);

  const showOnlyAllRowsAlert = allRowsInvalid && uniformErrors.length > 0;

  const showDuplicateList =
    hasDuplicates &&
    validationResult.totalValid > 0 &&
    validationResult.duplicateRows?.length > 0;

  const duplicateColumns = (config?.duplicateCheck?.fields || []).map((f) => {
    const col = config.templateColumns.find((c) => (c.fieldKey || c.label) === f);
    return { key: f, label: col ? t(col.label) : f };
  });
  const statusClass =
    hasImportErrors || hasDuplicates ? "text-danger" : "text-success";

  useEffect(() => {
    if (error) {
      toast.error(t(error) || error, { position: "top-right", autoClose: 4000 });
      setError(null);
    }
  }, [error]);

  const onFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const result = await handleFileSelect(file);
      if (!result) {
        return;
      }
      if (result?.data) {
        await validateFile(result.data);
      }
    } catch (err) {}
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={openDrawer}>
        <FontAwesomeIcon icon={faFileImport} className="me-2" />
        {t(config?.buttonKey)}
      </Button>

      <Offcanvas
        show={isOpen}
        onHide={closeDrawer}
        placement="end"
        backdrop={true}
        scroll={true}
        className="bulk-import-offcanvas"
      >
        <div className="d-flex flex-column h-100 bg-white">
          <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{t(config?.titleKey)}</h4>
            <Button
              variant="link"
              className="p-0 border-0 shadow-none text-secondary flex-shrink-0 fs-7"
              onClick={closeDrawer}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>

          <div className="p-3 overflow-auto flex-grow-1 scrollbar">
            {/* Loading Spinner */}
            {isLoading && (
              <div className="text-center py-5 bg-white border rounded shadow-sm my-auto">
                <Spinner
                  animation="border"
                  variant="primary"
                  role="status"
                  size="md"
                  className="mb-3"
                >
                  <span className="visually-hidden">{t("loading")}</span>
                </Spinner>
                <div className="text-muted fw-medium font-size-13">
                  {t("bulkImport.processing")}
                </div>
              </div>
            )}

            {!isLoading && (
              <>
                {/* Hidden file input */}
                <input
                  type="file"
                  id="import-file-input"
                  accept=".csv,.xlsx"
                  onChange={onFileSelected}
                  className="d-none"
                  key={isOpen ? "open" : "closed"}
                  onClick={(e) => {
                    e.target.value = null;
                  }}
                />

                {/* Drag & Drop Upload Box */}
                <label
                  htmlFor="import-file-input"
                  className={`import-dropzone bulk-import-dropzone d-flex flex-column align-items-center justify-content-center text-center rounded p-4 m-0 pointer${
                  selectedFile
                    ? hasImportErrors || hasDuplicates
                      ? " import-dropzone--error"
                      : " import-dropzone--selected"
                    : ""
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add("import-dropzone--active");
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove("import-dropzone--active");
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("import-dropzone--active");
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      onFileSelected({ target: { files: [file] } });
                    }
                  }}
                >
                  {selectedFile ? (
                    <>
                      <FontAwesomeIcon
                        icon={hasImportErrors ? faTimesCircle : hasDuplicates ? faExclamationTriangle : faCheckCircle}
                        className={`${statusClass} mb-2`}
                      />
                      <span className={`fw-semibold font-size-13 ${statusClass}`}>
                        {selectedFile.name}
                      </span>
                      <small className="text-muted mt-1 font-size-11">
                        {t("bulkImport.changeFile")}
                      </small>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={faCloudUploadAlt}
                        className="text-secondary mb-2"
                      />
                      <span className="fw-semibold font-size-13">
                        {t("bulkImport.dragDrop")}
                      </span>
                      <small className="text-muted my-1 font-size-11">{t("bulkImport.or")}</small>
                      <span className="text-primary fw-semibold font-size-13">
                        {t("bulkImport.selectFile")}
                      </span>

                      <small className="text-muted mt-3 font-size-11">
                        {t("bulkImport.formatsAccepted")}
                      </small>
                      <div className="d-flex gap-2 mt-1">
                        <span className="text-secondary fw-normal font-size-11 px-2 py-1">
                          CSV, Excel
                        </span>
                      </div>

                      {/* Template download */}
                      <div className="mt-3 pt-2 border-top w-100 text-center">
                        <small className="text-muted font-size-11">
                          {t("bulkImport.needTemplate")}{" "}
                          {t("bulkImport.downloadSample")}{" "}
                          <span
                            className="text-primary pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              downloadTemplate(config, "csv", t);
                            }}
                          >
                            CSV
                          </span>{" "}
                          {t("bulkImport.or")}{" "}
                          <span
                            className="text-primary pointer"
                            onClick={async (e) => {
                              e.preventDefault();
                              await downloadTemplate(config, "xlsx", t);
                            }}
                          >
                            Excel
                          </span>
                        </small>
                      </div>
                    </>
                  )}
                </label>
                {hasImportErrors && (
                  <Alert variant="danger" className="d-flex align-items-start gap-2 mt-3 mb-0 font-size-12">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mt-1 flex-shrink-0" />
                      {showOnlyAllRowsAlert ? (
                        <div>
                          {uniformErrors.map((msg) => (
                            <div key={msg}>
                              {t("bulkImport.allRowsIssue", {
                                message: msg,
                                count: validationResult.invalidRows.length,
                                interpolation: { escapeValue: false },
                              })}
                            </div>
                          ))}
                        </div>
                      ) : (
                      <span>{t("bulkImport.fixErrorsAndReupload")}</span>
                    )}
                  </Alert>
                )}
                {hasDuplicates && (
                  <Alert variant="danger" className="d-flex align-items-start gap-2 mt-3 mb-0 font-size-12">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1 flex-shrink-0" />
                    <span>
                      {validationResult.totalValid > 0
                        ? t("bulkImport.duplicatesSkipped", {
                            duplicates: validationResult.totalDuplicates,
                            valid: validationResult.totalValid,
                          })
                        : t("bulkImport.allDuplicates", {
                            count: validationResult.totalDuplicates,
                          })}
                    </span>
                  </Alert>
                )}
               </>
            )}
          </div>

          {validationResult?.invalidRows?.length > 0 && !showOnlyAllRowsAlert && (
            <div className="border-top import-error-list">
              <div className="bg-white sticky-top">
                <div className="fw-semibold font-size-13 text-danger px-3 pt-3 pb-1">
                  {validationResult.invalidRows.length} {t("bulkImport.failed")}
                </div>
                <div className="d-flex font-size-11 fw-semibold text-dark bg-light px-3 py-1 border-bottom">
                  <div className="import-error-row-col">{t("bulkImport.rowNumber")}</div>
                  <div className="flex-grow-1">{t("bulkImport.error")}</div>
                </div>
              </div>

              {/* Error rows */}
              <div className="px-3 pb-3">
                {validationResult.invalidRows.map((r) => (
                  <div
                    key={r.rowNumber}
                    className="d-flex font-size-11 text-muted py-1 border-bottom"
                  >
                    <div className="import-error-row-col">#{r.rowNumber}</div>
                    <div className="flex-grow-1">{(r.errors || []).join("; ")}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showDuplicateList && (
            <div className="border-top import-error-list">
              <div className="bg-white sticky-top">
                <div className="fw-semibold font-size-13 text-danger px-3 pt-3 pb-1">
                  {validationResult.duplicateRows.length} {t("bulkImport.duplicateFound")}
                </div>
                <div className="d-flex font-size-11 fw-semibold text-dark bg-light px-3 py-1 border-bottom">
                  <div className="import-error-row-col">{t("bulkImport.rowNumber")}</div>
                  {duplicateColumns.map((c) => (
                    <div key={c.key} className="flex-grow-1 w-50">{c.label}</div>
                  ))}
                </div>
              </div>

              <div className="px-3 pb-3">
                {validationResult.duplicateRows.map((r) => (
                  <div
                    key={r.rowNumber}
                    className="d-flex font-size-11 text-muted py-1 border-bottom"
                  >
                    <div className="import-error-row-col">#{r.rowNumber}</div>
                    {duplicateColumns.map((c) => (
                      <div key={c.key} className="flex-grow-1 w-50 text-break">
                        {r.displayData?.[c.key] ?? "-"}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-top p-3 bg-white">
            <div className="d-flex flex-column gap-2">
              <Button
                variant="primary"
                size="sm"
                className="d-flex align-items-center justify-content-center gap-2 w-100 fw-semibold rounded-3"
                onClick={executeImport}
                disabled={isLoading || !validationResult || validationResult.totalValid === 0 || hasImportErrors}
              >
                {t("bulkImport.import")}{!hasImportErrors && ` (${validationResult?.totalValid || 0})`}
              </Button>
            </div>
          </div>
        </div>
      </Offcanvas>
    </>
  );
};

export default BulkImportDrawer;